import { useEffect, useState, useRef } from 'react';
import api from '../api/apiClient';
import PageHeader from '../components/PageHeader';
import useAuth from '../hooks/useAuth';

export default function ChatPage() {
  const { user, currentBranch } = useAuth();
  const [threads, setThreads] = useState([]);
  const [selectedThread, setSelectedThread] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [isLoadingThreads, setIsLoadingThreads] = useState(true);
  const [isLoadingMessages, setIsLoadingMessages] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [error, setError] = useState('');
  const [previewImage, setPreviewImage] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);

  const [isMobile, setIsMobile] = useState(typeof window !== 'undefined' ? window.innerWidth < 768 : false);

  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    loadThreads();
  }, [currentBranch]);

  useEffect(() => {
    if (selectedThread) {
      loadMessages(selectedThread.id);
    }
  }, [selectedThread]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  async function loadThreads() {
    setIsLoadingThreads(true);
    setError('');
    try {
      const response = await api.get('/chat/threads');
      const data = response.data.data || [];
      setThreads(data);
      if (data.length > 0 && !selectedThread && window.innerWidth >= 768) {
        setSelectedThread(data[0]);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Unable to load chat threads');
    } finally {
      setIsLoadingThreads(false);
    }
  }

  async function loadMessages(threadId) {
    setIsLoadingMessages(true);
    try {
      const response = await api.get(`/chat/messages?thread_id=${threadId}`);
      setMessages(response.data.data || []);
    } catch (err) {
      setError(err.response?.data?.message || 'Unable to load messages');
    } finally {
      setIsLoadingMessages(false);
    }
  }

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedFile(file);
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if ((!newMessage.trim() && !selectedFile) || !selectedThread || isSending) return;

    setIsSending(true);
    setError('');

    try {
      let finalMessageText = newMessage.trim();

      if (selectedFile) {
        setIsUploading(true);
        const formData = new FormData();
        formData.append('file', selectedFile);

        try {
          const uploadRes = await api.post('/chat/upload', formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });
          const fileUrl = uploadRes.data.fileUrl || uploadRes.data.url;
          const sizeKb = (selectedFile.size / 1024).toFixed(1);
          
          const attachmentString = `Attached Document: ${selectedFile.name} (${sizeKb} KB) ${fileUrl}`;
          finalMessageText = finalMessageText
            ? `${attachmentString}\n${finalMessageText}`
            : attachmentString;
        } catch (uploadErr) {
          console.warn('Backend file upload endpoint failed, falling back to base64 encoding:', uploadErr);
          const reader = new FileReader();
          const base64Url = await new Promise((resolve) => {
            reader.onload = (ev) => resolve(ev.target.result);
            reader.readAsDataURL(selectedFile);
          });
          const sizeKb = (selectedFile.size / 1024).toFixed(1);
          const attachmentString = `Attached Document: ${selectedFile.name} (${sizeKb} KB) ${base64Url}`;
          finalMessageText = finalMessageText
            ? `${attachmentString}\n${finalMessageText}`
            : attachmentString;
        }
      }

      const response = await api.post('/chat/messages', {
        thread_id: selectedThread.id,
        message: finalMessageText
      });

      const sentMsg = response.data.data || {
        id: Date.now(),
        thread_id: selectedThread.id,
        sender_id: user.id,
        sender_role: user.role,
        sender_name: user.name || 'Admin',
        message: finalMessageText,
        created_at: new Date().toISOString()
      };

      setMessages((prev) => [...prev, sentMsg]);
      setNewMessage('');
      setSelectedFile(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
      loadThreads();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to send message');
    } finally {
      setIsSending(false);
      setIsUploading(false);
    }
  };

  const filteredThreads = threads.filter((t) => {
    const q = searchQuery.toLowerCase();
    return (
      (t.student_name && t.student_name.toLowerCase().includes(q)) ||
      (t.parent_name && t.parent_name.toLowerCase().includes(q)) ||
      (t.teacher_name && t.teacher_name.toLowerCase().includes(q)) ||
      (t.branch_name && t.branch_name.toLowerCase().includes(q))
    );
  });

  const getProperFileName = (fileUrl, customName) => {
    const urlPath = (fileUrl || '').split('?')[0];
    const urlFilename = urlPath.split('/').pop() || '';
    let ext = '';

    if (urlFilename.includes('.')) {
      ext = '.' + urlFilename.split('.').pop().toLowerCase();
    }

    if (!ext || ext.length > 5) {
      ext = '.pdf';
    }

    let name = (customName || '').trim();
    name = name.replace(/\s*\(\d+(\.\d+)?\s*[kKmMgG]?[bB]\)\s*/g, '').trim();

    if (!name || name.toLowerCase() === 'download document' || name.toLowerCase() === 'view document' || name.toLowerCase() === 'download') {
      name = urlFilename || 'document' + ext;
    }

    if (!name.toLowerCase().endsWith(ext)) {
      if (/\.[a-zA-Z0-9]{2,4}$/.test(name)) {
        name = name.replace(/\.[a-zA-Z0-9]{2,4}$/, ext);
      } else {
        name = name + ext;
      }
    }

    return name;
  };

  const handleDownload = (fileUrl, customName) => {
    if (!fileUrl) return;
    const fileName = getProperFileName(fileUrl, customName);

    // 1. If base64 data URL or blob URL -> download immediately!
    if (fileUrl.startsWith('data:') || fileUrl.startsWith('blob:')) {
      const a = document.createElement('a');
      a.href = fileUrl;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      return;
    }

    // 2. Fetch file via CORS proxy & FileReader to trigger native Save As dialog
    const proxyUrl = `https://corsproxy.io/?${encodeURIComponent(fileUrl)}`;

    fetch(proxyUrl)
      .then((res) => {
        if (!res.ok) throw new Error('Proxy error ' + res.status);
        return res.blob();
      })
      .then((blob) => {
        const reader = new FileReader();
        reader.onloadend = () => {
          const a = document.createElement('a');
          a.href = reader.result;
          a.download = fileName;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
        };
        reader.readAsDataURL(blob);
      })
      .catch(() => {
        // Fallback 1: Direct fetch
        fetch(fileUrl)
          .then((res) => res.blob())
          .then((blob) => {
            const reader = new FileReader();
            reader.onloadend = () => {
              const a = document.createElement('a');
              a.href = reader.result;
              a.download = fileName;
              document.body.appendChild(a);
              a.click();
              document.body.removeChild(a);
            };
            reader.readAsDataURL(blob);
          })
          .catch(() => {
            // Fallback 2: Canvas for images
            const img = new Image();
            img.src = fileUrl;
            img.onload = () => {
              try {
                const canvas = document.createElement('canvas');
                canvas.width = img.naturalWidth || img.width;
                canvas.height = img.naturalHeight || img.height;
                const ctx = canvas.getContext('2d');
                ctx.drawImage(img, 0, 0);

                const dataUrl = canvas.toDataURL('image/png');
                const a = document.createElement('a');
                a.href = dataUrl;
                a.download = fileName;
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
              } catch (e) {
                const a = document.createElement('a');
                a.href = fileUrl;
                a.download = fileName;
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
              }
            };
            img.onerror = () => {
              const a = document.createElement('a');
              a.href = fileUrl;
              a.download = fileName;
              document.body.appendChild(a);
              a.click();
              document.body.removeChild(a);
            };
          });
      });
  };

  const renderMessageContent = (messageText, isAdminRole) => {
    if (!messageText) return null;

    const urlRegex = /(https?:\/\/[^\s]+)/g;
    const matches = messageText.match(urlRegex);

    if (!matches || matches.length === 0) {
      return <div style={{ whiteSpace: 'pre-wrap' }}>{messageText}</div>;
    }

    const url = matches[0];
    const lowerUrl = url.toLowerCase();

    const isImageExt = /\.(jpg|jpeg|png|gif|webp|svg|bmp)(\?.*)?$/i.test(lowerUrl);
    const isAttachedImage = messageText.startsWith('Attached Document:') && /\.(jpg|jpeg|png|gif|webp|svg|bmp)/i.test(messageText);
    const isImage = isImageExt || isAttachedImage;

    const isPdf = /\.pdf(\?.*)?$/i.test(lowerUrl);
    const isDoc = messageText.startsWith('Attached Document:') || isPdf || /\.(doc|docx|xls|xlsx|ppt|pptx|zip|txt)(\?.*)?$/i.test(lowerUrl);

    let docInfoText = '';
    if (messageText.startsWith('Attached Document:')) {
      const urlIdx = messageText.indexOf('http');
      if (urlIdx !== -1) {
        docInfoText = messageText.substring(0, urlIdx).trim();
      }
    }

    if (isImage) {
      let caption = '';
      if (messageText.includes('\n')) {
        const lines = messageText.split('\n');
        caption = lines.filter(l => !l.startsWith('Attached Document:') && !/^https?:\/\//.test(l.trim())).join('\n').trim();
      }

      return (
        <div>
          <img
            src={url}
            alt="Chat Attachment"
            style={{
              maxWidth: '100%',
              maxHeight: '300px',
              borderRadius: '10px',
              cursor: 'pointer',
              display: 'block',
              objectFit: 'cover'
            }}
            onClick={() => setPreviewImage(url)}
          />
          {caption && (
            <div style={{ marginTop: '6px', fontSize: '14px', whiteSpace: 'pre-wrap' }}>
              {caption}
            </div>
          )}
        </div>
      );
    }

    if (isDoc) {
      const cleanLabel = docInfoText ? docInfoText.replace(/^Attached Document:\s*/, '') : 'Document.pdf';
      const finalDocName = getProperFileName(url, cleanLabel);
      
      let sizeStr = '';
      const sizeMatch = messageText.match(/\((\d+(\.\d+)?\s*[kKmMgG]?[bB])\)/);
      if (sizeMatch && sizeMatch[1]) {
        sizeStr = sizeMatch[1];
      }

      return (
        <div style={{ marginTop: '6px' }}>
          <div
            onClick={(e) => {
              e.stopPropagation();
              handleDownload(url, cleanLabel);
            }}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: '12px',
              padding: '10px 14px',
              borderRadius: '14px',
              background: isAdminRole ? 'rgba(255, 255, 255, 0.2)' : '#f5f3ff',
              border: isAdminRole ? '1px solid rgba(255, 255, 255, 0.35)' : '1px solid #e9d5ff',
              color: isAdminRole ? '#ffffff' : '#0f172a',
              cursor: 'pointer',
              transition: 'all 0.15s ease'
            }}
            title="Tap to Download Document"
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <div style={{
                background: '#fee2e2',
                border: '1px solid #fca5a5',
                color: '#dc2626',
                padding: '6px 10px',
                borderRadius: '8px',
                fontWeight: 'bold',
                fontSize: '12px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                minWidth: '40px'
              }}>
                {isPdf ? 'PDF' : 'DOC'}
              </div>

              <div>
                <div style={{
                  fontWeight: 700,
                  fontSize: '13px',
                  wordBreak: 'break-all',
                  color: isAdminRole ? '#ffffff' : '#1e1b4b'
                }}>
                  {finalDocName}
                </div>
                <div style={{
                  fontSize: '11px',
                  opacity: 0.85,
                  marginTop: '2px',
                  color: isAdminRole ? '#e0e7ff' : '#6b21a8'
                }}>
                  {sizeStr ? `${sizeStr} • ` : ''}Tap to Download {isPdf ? 'PDF' : 'File'}
                </div>
              </div>
            </div>

            <span style={{ fontSize: '18px', fontWeight: 'bold', opacity: 0.7 }}>›</span>
          </div>
        </div>
      );
    }

    const parts = messageText.split(urlRegex);
    return (
      <div style={{ whiteSpace: 'pre-wrap' }}>
        {parts.map((part, idx) => {
          if (/^https?:\/\//i.test(part)) {
            return (
              <a
                key={idx}
                href={part}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  color: isAdminRole ? '#93c5fd' : '#2563eb',
                  textDecoration: 'underline'
                }}
              >
                {part}
              </a>
            );
          }
          return part;
        })}
      </div>
    );
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', width: '100%' }}>
      {!isMobile && (
        <PageHeader
          title="Parent-Teacher Chat Monitoring"
          subtitle="Real-time supervision of parent-teacher communications, admin oversight & group compliance"
        />
      )}

      {error && <div className="error-message" style={{ margin: isMobile ? '8px 12px' : '0 0 12px' }}>{error}</div>}

      <div style={{
        display: 'flex',
        flex: 1,
        background: '#fff',
        borderRadius: isMobile ? '0' : '16px',
        border: isMobile ? 'none' : '1px solid #e2e8f0',
        boxShadow: isMobile ? 'none' : '0 4px 16px rgba(0,0,0,0.04)',
        overflow: 'hidden',
        minHeight: 0,
        height: '100%'
      }}>
        {/* Left Sidebar - Threads List */}
        {(!isMobile || !selectedThread) && (
          <div style={{
            width: isMobile ? '100%' : '340px',
            minWidth: isMobile ? '100%' : '300px',
            borderRight: isMobile ? 'none' : '1px solid #e2e8f0',
            display: 'flex',
            flexDirection: 'column',
            background: '#f8fafc',
            height: '100%'
          }}>
            <div style={{ padding: isMobile ? '10px 12px' : '14px 16px', borderBottom: '1px solid #e2e8f0', background: '#fff' }}>
              <input
                type="text"
                placeholder="🔍 Search parent, teacher, student..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                style={{
                  width: '100%',
                  padding: '9px 12px',
                  borderRadius: '10px',
                  border: '1px solid #cbd5e1',
                  fontSize: '14px',
                  outline: 'none',
                  background: '#f8fafc'
                }}
              />
            </div>

            <div style={{ flex: 1, overflowY: 'auto' }}>
              {isLoadingThreads ? (
                <div style={{ padding: '24px', textAlign: 'center', color: '#64748b', fontSize: 14 }}>
                  Loading conversations...
                </div>
              ) : filteredThreads.length === 0 ? (
                <div style={{ padding: '24px', textAlign: 'center', color: '#94a3b8', fontSize: 14 }}>
                  No chat conversations found.
                </div>
              ) : (
                filteredThreads.map((t) => {
                  const isSelected = selectedThread?.id === t.id;
                  return (
                    <div
                      key={t.id}
                      onClick={() => setSelectedThread(t)}
                      style={{
                        padding: '12px 14px',
                        borderBottom: '1px solid #f1f5f9',
                        cursor: 'pointer',
                        background: isSelected ? '#eff6ff' : '#fff',
                        borderLeft: isSelected ? '4px solid #2563eb' : '4px solid transparent',
                        transition: 'all 0.15s ease'
                      }}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                        <span style={{ fontWeight: 700, fontSize: 14, color: '#0f172a' }}>
                          🎓 {t.student_name || 'Student'}
                        </span>
                        <span style={{ fontSize: 11, color: '#94a3b8' }}>
                          {t.last_message_at ? new Date(t.last_message_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : ''}
                        </span>
                      </div>

                      <div style={{ fontSize: 12, color: '#475569', marginBottom: 6 }}>
                        👨‍👩‍👧 {t.parent_name || 'Parent'} &nbsp;|&nbsp; 👩‍🏫 {t.teacher_name || 'Teacher'}
                      </div>

                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span style={{
                          fontSize: 12,
                          color: '#64748b',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          maxWidth: isMobile ? '200px' : '180px'
                        }}>
                          {t.last_message || 'No messages yet'}
                        </span>

                        <span style={{
                          fontSize: '10px',
                          padding: '2px 6px',
                          background: '#dbeafe',
                          color: '#1d4ed8',
                          borderRadius: '10px',
                          fontWeight: 600
                        }}>
                          🛡️ Admins: {t.admin_count || 1}
                        </span>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        )}

        {/* Right Panel - Chat Message Transcript & Intervention Box */}
        {(!isMobile || selectedThread) && (
          selectedThread ? (
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', background: '#fff', height: '100%', minWidth: 0 }}>
              {/* Header with Admin Monitored Restriction Indicator */}
              <div style={{
                padding: isMobile ? '10px 14px' : '14px 20px',
                borderBottom: '1px solid #e2e8f0',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                background: '#ffffff',
                gap: 8,
                minHeight: '52px'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, flex: 1, minWidth: 0 }}>
                  {isMobile && (
                    <button
                      type="button"
                      onClick={() => setSelectedThread(null)}
                      title="Back to conversation list"
                      style={{
                        padding: '0',
                        width: '34px',
                        height: '34px',
                        borderRadius: '50%',
                        border: '1px solid #bfdbfe',
                        background: '#eff6ff',
                        color: '#1d4ed8',
                        fontSize: '18px',
                        fontWeight: 800,
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0
                      }}
                    >
                      ←
                    </button>
                  )}
                  <div style={{ minWidth: 0 }}>
                    <h3 style={{
                      margin: 0,
                      fontSize: isMobile ? '14px' : '16px',
                      fontWeight: 700,
                      color: '#0f172a',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis'
                    }}>
                      Student: {selectedThread.student_name}
                    </h3>
                    <div style={{
                      fontSize: isMobile ? '11px' : '12px',
                      color: '#64748b',
                      marginTop: 2,
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis'
                    }}>
                      👨‍👩‍👧 {selectedThread.parent_name || 'Parent'} • 👩‍🏫 {selectedThread.teacher_name || 'Teacher'}
                    </div>
                  </div>
                </div>

                {/* Total Admins in Group Indicator */}
                <div style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 4,
                  padding: '3px 8px',
                  background: '#eff6ff',
                  border: '1px solid #bfdbfe',
                  borderRadius: '16px',
                  color: '#1e40af',
                  fontSize: '11px',
                  fontWeight: 700,
                  whiteSpace: 'nowrap',
                  flexShrink: 0
                }}>
                  <span>🛡️</span>
                  <span>{selectedThread.admin_count || 1} Admins</span>
                </div>
              </div>

            {/* Messages Stream */}
            <div style={{ flex: 1, padding: isMobile ? '12px 14px' : '20px 24px', overflowY: 'auto', background: '#f1f5f9' }}>
              {isLoadingMessages ? (
                <div style={{ textAlign: 'center', color: '#64748b', padding: '40px' }}>
                  Loading chat transcript...
                </div>
              ) : messages.length === 0 ? (
                <div style={{ textAlign: 'center', color: '#94a3b8', padding: '40px' }}>
                  No messages exchanged in this conversation yet.
                </div>
              ) : (
                messages.map((m) => {
                  const isAdminRole = m.sender_role === 'admin' || m.sender_role === 'center_manager';
                  const isTeacherRole = m.sender_role === 'teacher';
                  const isImageMessage = (m.message.startsWith('Attached Document:') || /\.(jpg|jpeg|png|gif|webp|svg|bmp)(\?.*)?$/i.test(m.message)) && !m.message.includes('.pdf') && !m.message.includes('\n');

                  return (
                    <div
                      key={m.id}
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: isAdminRole ? 'flex-end' : (isTeacherRole ? 'flex-start' : 'flex-start'),
                        marginBottom: '14px'
                      }}
                    >
                      <div style={{ fontSize: '11px', color: '#64748b', marginBottom: '3px', padding: '0 4px' }}>
                        <strong>{m.sender_name}</strong> &nbsp;
                        <span style={{
                          textTransform: 'uppercase',
                          fontWeight: 700,
                          fontSize: '10px',
                          padding: '1px 5px',
                          borderRadius: '4px',
                          background: isAdminRole ? '#ede9fe' : (isTeacherRole ? '#d1fae5' : '#e0f2fe'),
                          color: isAdminRole ? '#6d28d9' : (isTeacherRole ? '#047857' : '#0369a1')
                        }}>
                          {m.sender_role}
                        </span>
                      </div>

                      <div style={{
                        maxWidth: '70%',
                        width: isImageMessage ? 'fit-content' : 'auto',
                        padding: isImageMessage ? '6px 6px 4px 6px' : '12px 16px',
                        borderRadius: '14px',
                        background: isImageMessage ? '#f3e8ff' : (isAdminRole ? '#2563eb' : (isTeacherRole ? '#fff' : '#e2e8f0')),
                        border: isImageMessage ? '1px solid #ddd6fe' : 'none',
                        color: isImageMessage ? '#1e1b4b' : (isAdminRole ? '#fff' : '#0f172a'),
                        boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
                        lineHeight: 1.5,
                        fontSize: '14px'
                      }}>
                        {renderMessageContent(m.message, isAdminRole)}
                        <div style={{
                          fontSize: '10px',
                          textAlign: 'right',
                          marginTop: '4px',
                          color: isImageMessage ? '#6b21a8' : (isAdminRole ? 'rgba(255,255,255,0.85)' : '#64748b'),
                          fontWeight: 500
                        }}>
                          {m.created_at ? new Date(m.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : ''}
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
              <div ref={messagesEndRef} />
            </div>

            {/* Selected File Banner */}
            {selectedFile && (
              <div style={{
                padding: '8px 18px',
                background: '#f5f3ff',
                borderTop: '1px solid #ddd6fe',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                fontSize: '13px',
                color: '#5b21b6'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', overflow: 'hidden', whiteSpace: 'nowrap' }}>
                  <span style={{ fontSize: '16px' }}>📎</span>
                  <strong style={{ overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '250px' }}>{selectedFile.name}</strong>
                  <span style={{ fontSize: '11px', opacity: 0.8 }}>({(selectedFile.size / 1024).toFixed(1)} KB)</span>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    setSelectedFile(null);
                    if (fileInputRef.current) fileInputRef.current.value = '';
                  }}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: '#ef4444',
                    fontWeight: 'bold',
                    cursor: 'pointer',
                    fontSize: '16px',
                    padding: '0 4px'
                  }}
                  title="Remove attachment"
                >
                  ✕
                </button>
              </div>
            )}

            {/* Admin Response / Intervention Input Form (Matching Mobile App Screenshot) */}
            <form onSubmit={handleSendMessage} style={{
              padding: '12px 18px',
              borderTop: '1px solid #e2e8f0',
              display: 'flex',
              gap: 12,
              alignItems: 'center',
              background: '#fff'
            }}>
              {/* Hidden File Input */}
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileChange}
                accept="image/*,application/pdf,.doc,.docx"
                style={{ display: 'none' }}
              />

              {/* Paperclip Attachment Icon Button */}
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                title="Attach Image or Document"
                style={{
                  background: selectedFile ? '#f3e8ff' : 'none',
                  border: 'none',
                  color: selectedFile ? '#6b21a8' : '#7c3aed',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: '8px',
                  borderRadius: '50%',
                  transition: 'all 0.15s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.color = '#6b21a8';
                  e.currentTarget.style.background = '#f3e8ff';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.color = selectedFile ? '#6b21a8' : '#7c3aed';
                  e.currentTarget.style.background = selectedFile ? '#f3e8ff' : 'none';
                }}
              >
                <svg
                  width="22"
                  height="22"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" />
                </svg>
              </button>

              {/* Rounded Pill Text Input */}
              <div style={{
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                background: '#f8fafc',
                border: '1px solid #ddd6fe',
                borderRadius: '24px',
                padding: '4px 16px',
                boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.03)'
              }}>
                <input
                  type="text"
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  placeholder={selectedFile ? "Add an optional caption..." : "Type a message..."}
                  style={{
                    width: '100%',
                    padding: '8px 0',
                    border: 'none',
                    outline: 'none',
                    background: 'transparent',
                    fontSize: '14px',
                    color: '#1e1b4b'
                  }}
                />
              </div>

              {/* Circular Purple Send Button (with Arrow icon ➤) */}
              <button
                type="submit"
                disabled={isSending || (!newMessage.trim() && !selectedFile)}
                title="Send Message"
                style={{
                  width: '44px',
                  height: '44px',
                  borderRadius: '50%',
                  background: isSending || (!newMessage.trim() && !selectedFile) ? '#c4b5fd' : '#4c1d95',
                  border: 'none',
                  color: '#ffffff',
                  fontSize: '18px',
                  fontWeight: 'bold',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: isSending || (!newMessage.trim() && !selectedFile) ? 'not-allowed' : 'pointer',
                  boxShadow: '0 3px 10px rgba(76, 29, 149, 0.3)',
                  transition: 'all 0.15s ease'
                }}
              >
                {isSending ? (
                  <span style={{ fontSize: '11px' }}>...</span>
                ) : (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z" />
                  </svg>
                )}
              </button>
            </form>
          </div>
        ) : (
          <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#94a3b8' }}>
            Select a conversation thread on the left to monitor messages.
          </div>
        ))}
      </div>

      {/* Full-screen Image Preview Modal (WhatsApp Style) */}
      {previewImage && (
        <div
          onClick={() => setPreviewImage(null)}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.85)',
            backdropFilter: 'blur(5px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 99999,
            padding: '20px',
            cursor: 'pointer'
          }}
        >
          {/* Close / Cut icon button */}
          <button
            onClick={() => setPreviewImage(null)}
            title="Close Preview"
            style={{
              position: 'absolute',
              top: '20px',
              right: '24px',
              background: 'rgba(255, 255, 255, 0.2)',
              border: 'none',
              color: '#ffffff',
              width: '42px',
              height: '42px',
              borderRadius: '50%',
              fontSize: '24px',
              fontWeight: 'bold',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              boxShadow: '0 4px 12px rgba(0,0,0,0.4)',
              transition: 'all 0.15s ease',
              lineHeight: 1
            }}
            onMouseEnter={(e) => (e.currentTarget.style.background = 'rgba(255, 255, 255, 0.4)')}
            onMouseLeave={(e) => (e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)')}
          >
            ✕
          </button>

          {/* Image Container */}
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              position: 'relative',
              maxWidth: '90vw',
              maxHeight: '90vh',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center'
            }}
          >
            <img
              src={previewImage}
              alt="Full Preview"
              style={{
                maxWidth: '90vw',
                maxHeight: '82vh',
                borderRadius: '10px',
                objectFit: 'contain',
                boxShadow: '0 12px 40px rgba(0,0,0,0.6)',
                border: '1px solid rgba(255,255,255,0.15)'
              }}
            />

            <div style={{ display: 'flex', gap: '12px', marginTop: '14px' }}>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleDownload(previewImage, 'chat-image.jpg');
                }}
                style={{
                  color: '#ffffff',
                  fontSize: '13px',
                  background: '#2563eb',
                  border: 'none',
                  padding: '8px 18px',
                  borderRadius: '20px',
                  fontWeight: 600,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  boxShadow: '0 2px 8px rgba(37, 99, 235, 0.4)'
                }}
              >
                📥 Download Image
              </button>

              <a
                href={previewImage}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                style={{
                  color: '#e2e8f0',
                  fontSize: '13px',
                  textDecoration: 'none',
                  background: 'rgba(255, 255, 255, 0.15)',
                  padding: '8px 16px',
                  borderRadius: '20px',
                  fontWeight: 500,
                  transition: 'background 0.15s ease'
                }}
                onMouseEnter={(e) => (e.currentTarget.style.background = 'rgba(255, 255, 255, 0.3)')}
                onMouseLeave={(e) => (e.currentTarget.style.background = 'rgba(255, 255, 255, 0.15)')}
              >
                🔗 Open Original ↗
              </a>

              <button
                onClick={() => setPreviewImage(null)}
                style={{
                  color: '#ffffff',
                  fontSize: '13px',
                  background: '#ef4444',
                  border: 'none',
                  padding: '8px 16px',
                  borderRadius: '20px',
                  fontWeight: 500,
                  cursor: 'pointer'
                }}
              >
                ✕ Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
